import { useAuth0 } from '@auth0/auth0-react';
import { FileClock, FileQuestion, Lock, TriangleAlert, UserX } from 'lucide-react';
import React, { useContext } from 'react';

import { myhiveLocalization, MyHiveLocalizationKey } from '../localization/my-hive.js';
import { LanguageContext } from '../providers/LanguageContext.js';

export enum ErrorType {
  PageNotFound = 'notFound',
  GenericError = 'genericError',
  LoadUserError = 'LoadUserError',
  HoneyLabelError = 'honeyLabelError',
  NoAccess = 'noAccess',
}

interface Props {
  errorType: ErrorType;
}

export const ErrorPage: React.FC<Props> = ({ errorType = ErrorType.GenericError }) => {
  switch (errorType) {
    case ErrorType.NoAccess:
      return <NoAccessError />;
    case ErrorType.PageNotFound:
      return <PageNotFoundError />;
    case ErrorType.LoadUserError:
      return <LoadUserErrorView />;
    case ErrorType.HoneyLabelError:
      return <HoneyLabelErrorView />;
    case ErrorType.GenericError:
    default:
      return <GenericErrorView />;
  }
};

const GenericErrorView: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="flex max-w-md flex-col items-center text-center">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
          <TriangleAlert size={24} />
        </div>
        <h1 className="mb-2 text-xl font-semibold text-gray-900">{t[MyHiveLocalizationKey.ErrorPageGenericTitle]}</h1>
        <p className="mb-6 text-sm leading-relaxed text-gray-600">{t[MyHiveLocalizationKey.ErrorPageGenericBody]}</p>
        <a
          href={t[MyHiveLocalizationKey.ErrorPageSupportUrl]}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-10 items-center justify-center rounded-md border border-alveole-teal px-6 text-sm font-medium text-alveole-teal transition-colors hover:bg-alveole-teal/5"
        >
          {t[MyHiveLocalizationKey.ErrorPageContactSupport]}
        </a>
      </div>
    </div>
  );
};

const LoadUserErrorView: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="flex max-w-md flex-col items-center text-center">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
          <UserX size={24} />
        </div>
        <h1 className="mb-2 text-xl font-semibold text-gray-900">{t[MyHiveLocalizationKey.ErrorPageLoadUserTitle]}</h1>
        <p className="mb-6 text-sm leading-relaxed text-gray-600">{t[MyHiveLocalizationKey.ErrorPageLoadUserBody]}</p>
        <a
          href={t[MyHiveLocalizationKey.ErrorPageSupportUrl]}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-10 items-center justify-center rounded-md border border-alveole-teal px-6 text-sm font-medium text-alveole-teal transition-colors hover:bg-alveole-teal/5"
        >
          {t[MyHiveLocalizationKey.ErrorPageContactSupport]}
        </a>
      </div>
    </div>
  );
};

const HoneyLabelErrorView: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="flex max-w-md flex-col items-center text-center">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-alveole-gold/15 text-alveole-gold">
          <FileClock size={24} />
        </div>
        <h1 className="mb-2 text-xl font-semibold text-gray-900">
          {t[MyHiveLocalizationKey.ErrorPageHoneyLabelTitle]}
        </h1>
        <p className="mb-6 text-sm leading-relaxed text-gray-600">{t[MyHiveLocalizationKey.ErrorPageHoneyLabelBody]}</p>
        <a
          href={t[MyHiveLocalizationKey.ErrorPageSupportUrl]}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-10 items-center justify-center rounded-md border border-alveole-teal px-6 text-sm font-medium text-alveole-teal transition-colors hover:bg-alveole-teal/5"
        >
          {t[MyHiveLocalizationKey.ErrorPageContactSupport]}
        </a>
      </div>
    </div>
  );
};

const PageNotFoundError: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="flex max-w-md flex-col items-center text-center">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-gray-500">
          <FileQuestion size={24} />
        </div>
        <h1 className="mb-2 text-xl font-semibold text-gray-900">{t[MyHiveLocalizationKey.ErrorPageNotFoundTitle]}</h1>
        <p className="mb-6 text-sm leading-relaxed text-gray-600">{t[MyHiveLocalizationKey.ErrorPageNotFoundBody]}</p>
        <a
          href={t[MyHiveLocalizationKey.ErrorPageSupportUrl]}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-10 items-center justify-center rounded-md border border-alveole-teal px-6 text-sm font-medium text-alveole-teal transition-colors hover:bg-alveole-teal/5"
        >
          {t[MyHiveLocalizationKey.ErrorPageContactSupport]}
        </a>
      </div>
    </div>
  );
};

const NoAccessError: React.FC = () => {
  const { isAuthenticated } = useAuth0();
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="flex max-w-lg flex-col items-center text-center">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-alveole-teal/10 text-alveole-teal">
          <Lock size={24} />
        </div>
        <h1 className="mb-2 text-xl font-semibold text-gray-900">{t[MyHiveLocalizationKey.ErrorPageNoAccessTitle]}</h1>
        <p className={`text-sm leading-relaxed text-gray-600${isAuthenticated ? ' mb-6' : ''}`}>
          {isAuthenticated
            ? t[MyHiveLocalizationKey.ErrorPageNoAccessAuthed]
            : t[MyHiveLocalizationKey.ErrorPageNoAccessGuest]}
        </p>
        {isAuthenticated && (
          <a
            href={t[MyHiveLocalizationKey.ErrorPageSupportUrl]}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 items-center justify-center rounded-md border border-alveole-teal px-6 text-sm font-medium text-alveole-teal transition-colors hover:bg-alveole-teal/5"
          >
            {t[MyHiveLocalizationKey.ErrorPageContactSupport]}
          </a>
        )}
      </div>
    </div>
  );
};
